import { z } from 'zod';
import { READ_UNCOVERED_MARKERS } from './planner-first-coverage.js';
import { READ_ALIASES as aliases, resolvePlannerTypedValue } from './planner-first-typed-values.js';
import type { IntentEnvelopeV1, MissingInformation } from './intent-envelope.js';
import { verifyOperationProvenance } from './intent-verifier.js';

export const READ_REPAIR_RULE =
  'For a read request preserve kind:read with complete envelope and read. Use singular entity task/deal/document/contact/company, mode:crm_read, target:null and fields:[], relations:[], dependsOn:[], missingInformation:[], unsupportedRequirements:[]. Never change a read into a write to repair its schema. Preserve literal names and all filters; unsupported filters require unsupported for the whole request.';

const literal = z.string().trim().min(1).max(200);
const quoted = <T extends z.ZodTypeAny>(value: T) =>
  z.object({ value, rawValue: literal, sourceQuote: z.string().min(1).max(2000) }).strict();
const name = quoted(literal);
const mine = quoted(z.literal('mine'));
const stage = quoted(z.enum(['New', 'Contacted', 'Negotiation', 'Won', 'Lost']));
const amount = quoted(z.number().finite().nonnegative().max(9_999_999_999.99));

/** Read parameters are separate from write fields; no IDs or raw query language. */
export const plannerFirstReadSchema = z.discriminatedUnion('capability', [
  z
    .object({
      capability: z.literal('tasks'),
      query: name.optional(),
      period: quoted(z.enum(['today', 'tomorrow', 'week', 'overdue'])).optional(),
      assignee: name.optional(),
      owner: mine.optional(),
      status: quoted(z.enum(['todo', 'in_progress', 'blocked', 'done', 'cancelled'])).optional(),
    })
    .strict(),
  z
    .object({
      capability: z.literal('deals'),
      query: name.optional(),
      stage: stage.optional(),
      owner: z.union([mine, quoted(z.literal('unassigned')), name]).optional(),
      inactiveDays: quoted(z.number().int().min(1).max(365)).optional(),
      amount: amount.optional(),
      noAmount: quoted(z.literal(true)).optional(),
      noNextStep: quoted(z.literal(true)).optional(),
    })
    .strict(),
  z
    .object({
      capability: z.literal('hot_leads'),
      stage: stage.optional(),
      minAmount: amount.optional(),
    })
    .strict(),
  z
    .object({
      capability: z.literal('entity_summary'),
      entity: z.enum(['contact', 'company', 'deal']),
      name,
    })
    .strict(),
  z
    .object({
      capability: z.literal('documents'),
      query: name.optional(),
      company: name.optional(),
      deal: name.optional(),
    })
    .strict(),
]);
export type PlannerFirstRead = z.infer<typeof plannerFirstReadSchema>;
export const PLANNER_READ_CAPS = Object.freeze({
  tasks: 10,
  deals: 20,
  hot_leads: 20,
  entity_summary: 1,
  documents: 20,
});

const norm = (s: string) => s.toLocaleLowerCase().replaceAll('ё', 'е').trim();

/** Exact literal matching with shared Unicode word boundaries and regex escaping. */
export function containsWholeToken(source: string, token: string): boolean {
  return wholeTokenPattern(norm(token)).test(norm(source));
}
function wholeTokenPattern(token: string): RegExp {
  const escaped = token.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');
  return new RegExp('(^|[^\\p{L}\\p{N}])' + escaped + '(?=$|[^\\p{L}\\p{N}])', 'u');
}

export function plannerReadEntity(
  read: PlannerFirstRead,
): 'task' | 'deal' | 'contact' | 'company' | 'document' {
  return read.capability === 'entity_summary'
    ? read.entity
    : read.capability === 'tasks'
      ? 'task'
      : read.capability === 'documents'
        ? 'document'
        : 'deal';
}

/** Every executable value has a literal quote and a server-checked interpretation. */
export function plannerReadSourceValid(read: PlannerFirstRead, source: string): boolean {
  if (read.capability === 'tasks' && read.query) {
    const query = norm(read.query.value);
    for (const period of ['today', 'tomorrow', 'week', 'overdue']) {
      for (const alias of aliases[period] ?? []) {
        if (wholeTokenPattern(norm(alias)).test(query)) {
          return false;
        }
      }
    }
  }
  for (const [key, fact] of Object.entries(read)) {
    if (typeof fact !== 'object' || fact === null) continue;
    const { value, rawValue, sourceQuote } = fact;
    if (!source.includes(sourceQuote) || !sourceQuote.includes(rawValue)) return false;
    const exactToken =
      typeof value === 'boolean' ||
      typeof value === 'number' ||
      ['period', 'status', 'stage'].includes(key) ||
      (key === 'owner' && ['mine', 'unassigned'].includes(value));
    if (
      exactToken &&
      (!wholeTokenPattern(rawValue).test(source) || !wholeTokenPattern(rawValue).test(sourceQuote))
    )
      return false;
    if (
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      ['period', 'status', 'stage'].includes(key) ||
      (key === 'owner' && ['mine', 'unassigned'].includes(value))
    ) {
      if (resolvePlannerTypedValue(key, rawValue) !== value) return false;
    } else if (value !== rawValue) return false;
  }
  if (read.capability === 'deals' && read.amount && read.noAmount) return false;
  if (read.capability === 'tasks' && read.owner && read.assignee) return false;
  if (read.capability === 'documents' && read.company && read.deal) return false;
  return true;
}

/**
 * Request-level completeness: every executable value is proven by the source and no recognized
 * filter or write wording is left outside the typed read. Independent of any envelope.
 */
export function plannerReadRequestValid(read: PlannerFirstRead, source: string): boolean {
  let remainder = source;
  for (const [key, fact] of Object.entries(read))
    if (key !== 'query' && typeof fact === 'object' && fact !== null) {
      remainder = remainder.replace(wholeTokenPattern(fact.rawValue), '$1 ');
    }
  // Recognized filter words left outside the proven parameters must not be dropped.
  const unrepresented = norm(remainder);
  if (
    Object.values(aliases)
      .flat()
      .some((alias) => wholeTokenPattern(norm(alias)).test(unrepresented))
  )
    return false;
  if (read.capability === 'deals' && read.inactiveDays)
    remainder = remainder.replace(
      /(^|[^\p{L}\p{N}])(?:без активности(?:\s+\d+)?(?:\s*дн[а-яё]*)?|inactive(?:\s+for)?(?:\s+\d+)?(?:\s*days)?)(?=$|[^\p{L}\p{N}])/iu,
      '$1 ',
    );
  if (READ_UNCOVERED_MARKERS.test(remainder)) return false;
  return plannerReadSourceValid(read, source);
}

/** Envelope-level shape: the duplicate envelope may only restate the typed read. */
export function plannerReadEnvelopeShapeValid(
  read: PlannerFirstRead,
  envelope: IntentEnvelopeV1,
  source: string,
): boolean {
  const op = envelope.operations[0];
  return (
    envelope.mode === 'crm_read' &&
    envelope.confidence !== 'low' &&
    envelope.operations.length === 1 &&
    envelope.missingInformation.length === 0 &&
    envelope.unsupportedRequirements.length === 0 &&
    op !== undefined &&
    op.entity === plannerReadEntity(read) &&
    ['read', 'search'].includes(op.operation) &&
    op.target === null &&
    op.dependsOn.length === 0 &&
    op.fields.length === 0 &&
    op.relations.length === 0 &&
    verifyOperationProvenance(source, op).ok
  );
}

export function plannerReadEnvelopeValid(
  read: PlannerFirstRead,
  envelope: IntentEnvelopeV1,
  source: string,
): boolean {
  return (
    plannerReadRequestValid(read, source) && plannerReadEnvelopeShapeValid(read, envelope, source)
  );
}

function readOperationEntity(capability: string, entity: unknown): string | undefined {
  if (capability === 'entity_summary') return typeof entity === 'string' ? entity : undefined;
  return capability === 'tasks'
    ? 'task'
    : capability === 'documents'
      ? 'document'
      : capability === 'deals' || capability === 'hot_leads'
        ? 'deal'
        : undefined;
}

function serverReadEnvelopeFor<E extends string>(entity: E, capability: string, source: string) {
  return {
    version: 1 as const,
    mode: 'crm_read' as const,
    confidence: 'high' as const,
    operations: [
      {
        clientOperationId: 'read',
        entity,
        operation: capability === 'entity_summary' ? ('read' as const) : ('search' as const),
        target: null,
        fields: [],
        relations: [],
        dependsOn: [],
        sourceQuote: source,
      },
    ],
    missingInformation: [],
    unsupportedRequirements: [],
  };
}

/** ADR-0006 E1: the executable read envelope is built by the server from the typed read. */
export function plannerServerReadEnvelope(
  read: PlannerFirstRead,
  source: string,
): IntentEnvelopeV1 {
  return serverReadEnvelopeFor(plannerReadEntity(read), read.capability, source);
}

/** True when a supplied literal duplicate contradicts its rawValue (entity_summary is derived). */
export function plannerReadLiteralConflict(read: PlannerFirstRead): boolean {
  const keys = LITERAL_READ_KEYS[read.capability] ?? [];
  if (read.capability === 'entity_summary') return false;
  return keys.some((key) => {
    const fact = (read as Record<string, unknown>)[key];
    return isRecord(fact) && fact['value'] !== fact['rawValue'];
  });
}

/** An unknown/advisory request is unsupported, not a request for arbitrary CRM details. */
export function plannerClarificationAllowed(
  source: string,
  missing: readonly MissingInformation[],
): boolean {
  const text = norm(source);
  if (!plannerClarificationSourceAllowed(source)) return false;
  const taskCreate = /^(?:создай|создать|поставь|добавь|create|add)\s+(?:задачу|task)\s*$/u.test(
    text,
  );
  const dealCreate = /^(?:создай|создать|добавь|create|add)\s+(?:сделку|deal)\s*$/u.test(text);
  const namedRead =
    /^(?:покажи|прочитай|расскажи про|расскажи о|show|read)\s+(?:контакт|компанию|компании|сделку|contact|company|deal)\s*$/u.test(
      text,
    );
  // Candidate ambiguities are generated by scoped resolution, not by model prose.
  return (
    missing.length > 0 &&
    missing.every(
      (m) =>
        (m.field === 'title' && (taskCreate || dealCreate)) || (m.field === 'name' && namedRead),
    )
  );
}

export const PLANNER_FIRST_READ_GUIDANCE = [
  'Additional simple READ form: {kind:"simple",envelope:IntentEnvelopeV1,read:ReadRequest}. Envelope: crm_read, high confidence, exactly one read/search of the corresponding entity, target:null, fields:[],relations:[],dependsOn:[], missingInformation:[],unsupportedRequirements:[].',
  'Every provided read filter is {value:typed value,rawValue:literal text,sourceQuote:literal enclosing quote}; no IDs, SQL, raw filters or ISO dates. Omit unused filters; never silently drop a condition or write intent.',
  'For tasks, query is only a literal task-title search, never period/filter wording. Put today/tomorrow/week/overdue wording only in period; omit query when no separate title search was requested. If a literal title itself contains period wording and cannot be represented unambiguously, return unsupported.',
  'ReadRequest strict catalog: tasks {query?:literal,period?:today|tomorrow|week|overdue,assignee?:literal member name,owner?:mine,status?:todo|in_progress|blocked|done|cancelled}; deals {query?:literal,stage?:New|Contacted|Negotiation|Won|Lost,owner?:mine|unassigned|literal member name,inactiveDays?:integer 1..365,amount?:nonnegative number,noAmount?:true,noNextStep?:true}; hot_leads {stage?,minAmount?:nonnegative number}; entity_summary {entity:contact|company|deal,name:literal}; documents {query?:literal,company?:literal OR deal?:literal}. Each object includes capability with the catalog name. Strings marked literal must equal rawValue. Row caps: tasks 10, deals/documents/hot_leads 20. Mine is resolved by the server, never a supplied user ID. No amount means zero stored amount. Collection reads do not require a record title. Entity summaries and relation names resolve against real scoped records; multiple matches cause server clarification.',
  'Catalog notation above lists values, not wire shapes: every filter/name is a nested fact, e.g. {"value":"today","rawValue":"today","sourceQuote":"today"}; never a bare scalar. Exactly one read/search operation is required; its target is null. Names belong in read.name. Never infer a numeric period or a stage from qualitative wording, or omit unsupported conditions.',
  'Clarification ONLY for an in-catalog missing REQUIRED parameter. Model clarification names that parameter in missingInformation; title is accepted only for an explicit task/deal creation with no title, name only for an explicit single contact/company/deal read with no name. Never ask arbitrary details for advice, drafting, analysis, outside-catalog requests or a broad collection read. Return unsupported for those. Do not invent candidates: the server resolves real ambiguity.',
  'The read form always requires one envelope operation, even when read contains every parameter: {kind:"simple",envelope:{version:1,mode:"crm_read",confidence:"high",operations:[{clientOperationId:"read",entity:"task",operation:"search",target:null,fields:[],relations:[],dependsOn:[],sourceQuote:"today"}],missingInformation:[],unsupportedRequirements:[]},read:{capability:"tasks",period:{value:"today",rawValue:"today",sourceQuote:"today"}}}. The operation sourceQuote must literally occur in the current request. Map capability tasks to entity task, deals/hot_leads to deal, documents to document, entity_summary to its entity. Never use plural capability names as operation entities.',
  'For the read form, target:null overrides the ordinary named-target guidance above: put the exact literal name only in read.name (or company/deal). Do not lemmatize, decline, translate or otherwise rewrite value relative to rawValue; the server resolves names. Hot means capability hot_leads, never a stage named hot. Supported stage values are New, Contacted, Negotiation, Won, Lost. The current hot_leads catalog has no owner or period filter; requests needing those remain unsupported with the literal unmet requirements. Never substitute today for a narrower unsupported time bound.',
  'Unsupported may include wanted:{entity,operation,reason} (short strings) for diagnostics ONLY; it grants no execution. Always retain literal unsupported requirements.',
].join('\n');

export function plannerClarificationSourceAllowed(source: string): boolean {
  const text = norm(source);
  if (
    /(?:посоветуй|совет|проанализируй|анализ|напиши письмо|составь письмо|погода|расскажи анекдот|advice|analy[sz]e|draft|weather)/u.test(
      text,
    )
  )
    return false;
  return true;
}

/** The duplicate display value is server-derived; only the quoted literal is search authority.
 * This narrow entity-summary decoder never drops filters/keys or repairs an invalid envelope.
 */
export function derivePlannerFirstSummaryName(raw: unknown, source: string): unknown {
  if (
    !raw ||
    typeof raw !== 'object' ||
    Array.isArray(raw) ||
    !('kind' in raw) ||
    !['read', 'simple'].includes(String(raw.kind)) ||
    !('read' in raw) ||
    !raw.read ||
    typeof raw.read !== 'object' ||
    Array.isArray(raw.read)
  )
    return raw;
  const read = raw.read;
  if (
    !('capability' in read) ||
    read.capability !== 'entity_summary' ||
    !('name' in read) ||
    !read.name ||
    typeof read.name !== 'object' ||
    Array.isArray(read.name)
  )
    return raw;
  const name = read.name;
  if (
    !('value' in name) ||
    typeof name.value !== 'string' ||
    !('rawValue' in name) ||
    typeof name.rawValue !== 'string' ||
    !('sourceQuote' in name) ||
    typeof name.sourceQuote !== 'string' ||
    !source.includes(name.sourceQuote) ||
    !containsWholeToken(name.sourceQuote, name.rawValue) ||
    !containsWholeToken(source, name.rawValue)
  )
    return raw;
  return { ...raw, read: { ...read, name: { ...name, value: name.rawValue } } };
}

/** Literal-name read facts: the server, not the model, owns `value` (always equal to rawValue). */
const LITERAL_READ_KEYS: Readonly<Record<string, readonly string[]>> = Object.freeze({
  tasks: ['query', 'assignee'],
  deals: ['query'],
  entity_summary: ['name'],
  documents: ['query', 'company', 'deal'],
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * ADR-0006 E1 literal-name wire decoder. For literal read facts an omitted `value` is derived
 * from `rawValue`. A supplied `value` is left untouched, so a conflicting duplicate is still
 * rejected by plannerReadSourceValid (or replaced by the entity-summary rule below). Enum,
 * numeric and owner facts are never derived: their typed value carries meaning beyond the literal.
 */
export function derivePlannerFirstLiteralValues(raw: unknown): unknown {
  if (!isRecord(raw) || !['read', 'simple', 'clarification'].includes(String(raw['kind'])))
    return raw;
  const read = raw['read'];
  if (!isRecord(read) || typeof read['capability'] !== 'string') return raw;
  const keys = LITERAL_READ_KEYS[read['capability']];
  if (!keys) return raw;
  let changed = false;
  const next: Record<string, unknown> = { ...read };
  for (const key of keys) {
    const fact = read[key];
    if (!isRecord(fact) || 'value' in fact || typeof fact['rawValue'] !== 'string') continue;
    next[key] = { ...fact, value: fact['rawValue'] };
    changed = true;
  }
  return changed ? { ...raw, read: next } : raw;
}

/** Single wire entry point for planner-first decisions before strict schema parsing. */
export function decodePlannerFirstReadWire(raw: unknown, source: string): unknown {
  return derivePlannerFirstSummaryName(
    derivePlannerFirstReadEnvelope(
      derivePlannerFirstTypedValues(derivePlannerFirstLiteralValues(raw)),
      source,
    ),
    source,
  );
}

const TYPED_READ_KEYS: Readonly<Record<string, readonly string[]>> = {
  tasks: ['period', 'status', 'owner'],
  deals: ['stage', 'owner', 'inactiveDays', 'amount', 'noAmount', 'noNextStep'],
  hot_leads: ['stage', 'minAmount'],
};

export function derivePlannerFirstTypedValues(raw: unknown): unknown {
  if (!isRecord(raw) || !['read', 'simple', 'clarification'].includes(String(raw['kind'])))
    return raw;
  const read = raw['read'];
  if (!isRecord(read) || typeof read['capability'] !== 'string') return raw;
  let changed = false;
  const next = { ...read };
  for (const key of TYPED_READ_KEYS[read['capability']] ?? []) {
    const fact = read[key];
    if (!isRecord(fact) || 'value' in fact || typeof fact['rawValue'] !== 'string') continue;
    const value = resolvePlannerTypedValue(key, fact['rawValue']);
    if (value === undefined) continue;
    next[key] = { ...fact, value };
    changed = true;
  }
  return changed ? { ...raw, read: next } : raw;
}

export function plannerReadTypedConflict(read: PlannerFirstRead): boolean {
  return (TYPED_READ_KEYS[read.capability] ?? []).some((key) => {
    const fact = (read as Record<string, unknown>)[key];
    if (!isRecord(fact) || typeof fact['rawValue'] !== 'string') return false;
    const derived = resolvePlannerTypedValue(key, fact['rawValue']);
    return derived !== undefined && fact['value'] !== derived;
  });
}

export function plannerReadRawTypedConflict(raw: unknown): boolean {
  if (!isRecord(raw) || !isRecord(raw['read'])) return false;
  const read = raw['read'];
  if (typeof read['capability'] !== 'string') return false;
  return (TYPED_READ_KEYS[read['capability']] ?? []).some((key) => {
    const fact = read[key];
    if (!isRecord(fact) || !('value' in fact) || typeof fact['rawValue'] !== 'string') return false;
    if (key === 'owner' && fact['value'] === fact['rawValue']) return false;
    const derived = resolvePlannerTypedValue(key, fact['rawValue']);
    return derived !== undefined && fact['value'] !== derived;
  });
}

/**
 * ADR-0006 E1 compatibility decoder: a typed read without the duplicate envelope receives the
 * server envelope. A supplied envelope is kept for validation and must restate the read.
 */
export function derivePlannerFirstReadEnvelope(raw: unknown, source: string): unknown {
  if (!isRecord(raw) || !['read', 'simple'].includes(String(raw['kind'])) || 'envelope' in raw)
    return raw;
  const read = raw['read'];
  if (!isRecord(read) || typeof read['capability'] !== 'string') return raw;
  const entity = readOperationEntity(read['capability'], read['entity']);
  if (entity === undefined) return raw;
  return { ...raw, envelope: serverReadEnvelopeFor(entity, read['capability'], source) };
}
